import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { istAufgebaut, readData } from "@/office/excel/daten";
import { allTables, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionPivotBauen: Lesson = {
  id: "pivot-bauen",
  title: "Die erste PivotTable",
  summary: "Zusammenfassen ohne eine einzige Formel – und ohne die Rohdaten anzufassen.",
  durationMinutes: 14,
  learningGoals: [
    "Eine PivotTable aus einer Tabelle erzeugen",
    "Felder auf Zeilen, Spalten, Werte und Filter verteilen",
    "Die Anforderungen an die Datenbasis benennen",
  ],
  steps: [
    {
      id: "was-ist-pivot",
      title: "Was eine PivotTable eigentlich tut",
      explanation: [
        "Bis hierher hast du Auswertungen gebaut: SUMMEWENN für eine Region, noch " +
          "eines für die nächste, darunter die Summe. Bei vier Regionen ist das " +
          "machbar, bei vierzig Produkten wird es zur Fleißarbeit – und bei jeder " +
          "neuen Kategorie muss eine Formel dazu.",
        "Eine PivotTable dreht die Aufgabe um. Statt für jede Kombination eine Formel " +
          "zu schreiben, sagt man ihr, welches Feld die Zeilen bestimmt, welches die " +
          "Spalten und was gerechnet werden soll. Den Rest erledigt sie – " +
          "einschließlich der Frage, welche Werte in den Kategorien überhaupt " +
          "vorkommen.",
        "Der Name kommt vom englischen „pivot“, dem Drehpunkt. Genau das ist die " +
          "Idee: Dieselben Daten lassen sich in Sekunden anders herum betrachten. " +
          "Region in den Zeilen und Monat in den Spalten – oder umgekehrt, per " +
          "Ziehen mit der Maus. Das macht die PivotTable zum Werkzeug fürs Erkunden, " +
          "nicht nur fürs Berichten.",
        "Und ein Punkt, der in der Praxis den Ausschlag gibt: Eine PivotTable ändert " +
          "die Rohdaten nicht. Sie liest sie und legt das Ergebnis woanders ab. Man " +
          "kann also nichts kaputt machen, und wenn das Ergebnis nicht überzeugt, " +
          "löscht man die PivotTable und fängt neu an.",
      ],
      keyPoints: [
        "Eine PivotTable fasst zusammen, ohne dass eine Formel geschrieben wird.",
        "Zeilen, Spalten, Werte und Filter sind die vier Bereiche.",
        "Die Rohdaten bleiben unberührt – man kann nichts zerstören.",
      ],
    },
    {
      id: "voraussetzungen",
      title: "Was die Datenbasis mitbringen muss",
      explanation: [
        "Eine PivotTable stellt Anforderungen an ihre Datenbasis, und sie sind genau " +
          "die, die dieser Kurs von Anfang an aufbaut. Eine Kopfzeile mit eindeutigen, " +
          "gefüllten Überschriften – aus ihnen entstehen die Feldnamen. Keine " +
          "Leerzeilen und Leerspalten. Keine verbundenen Zellen. Und je Spalte ein " +
          "einheitlicher Datentyp.",
        "Fehlt eine Spaltenüberschrift, verweigert Excel den Dienst mit einer " +
          "Fehlermeldung. Stehen in einer Zahlenspalte einzelne Textwerte, bietet die " +
          "PivotTable statt „Summe“ die „Anzahl“ an – der zuverlässigste Hinweis " +
          "darauf, dass in der Spalte etwas nicht stimmt. Wer sich wundert, warum " +
          "seine Beträge plötzlich als Stückzahlen erscheinen, hat genau dieses " +
          "Problem.",
        "Am besten steht als Basis eine Tabelle im Sinne von Modul 5. Dann ist der " +
          "Bereich benannt, und beim Aktualisieren nimmt die PivotTable neue Zeilen " +
          "automatisch mit. Bei einem festen Bereich muss man die Datenquelle von Hand " +
          "erweitern – und vergisst es.",
        "Ein häufiger Stolperstein bleibt trotzdem: Die PivotTable rechnet sich nicht " +
          "von selbst neu. Ändern sich die Rohdaten, braucht sie ein „Aktualisieren“ " +
          "über Rechtsklick oder Alt + F5. Das ist kein Fehler, sondern eine " +
          "Geschwindigkeitsentscheidung – bei großen Datenmengen wäre laufendes " +
          "Nachrechnen unerträglich langsam.",
      ],
      keyPoints: [
        "Kopfzeile vollständig, keine Leerzeilen, keine verbundenen Zellen.",
        "Bietet Excel „Anzahl“ statt „Summe“ an, steckt Text in der Zahlenspalte.",
        "Alt + F5 aktualisiert – von selbst rechnet eine PivotTable nicht nach.",
      ],
      task: {
        instruction:
          "Stelle sicher, dass deine Datenbasis pivot-tauglich ist: eine Tabelle mit " +
          "vollständiger Kopfzeile, in der keine Spaltenüberschrift fehlt.",
        hint: "Cursor in die Daten, Strg + T. Danach prüfen, ob jede Spalte eine Überschrift hat.",
        check: async () => {
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle als Datenbasis.",
              "Setze den Cursor in deine Daten und drücke Strg + T. Eine Tabelle als Basis sorgt dafür, " +
                "dass die PivotTable beim Aktualisieren neue Zeilen mitnimmt.",
            );
          }

          const groesste = [...tabellen].sort((a, b) => b.columns.length - a.columns.length)[0];
          if (groesste.columns.length === 0) {
            return nichtPruefbar(
              "Die Spalten einer Tabelle",
              "1.1",
              "Sieh die Kopfzeile deiner Tabelle durch: Trägt jede Spalte eine Überschrift?",
              true,
            );
          }

          const leere = groesste.columns.filter((spalte) => spalte.name.trim().length === 0);
          const standardnamen = groesste.columns.filter((spalte) =>
            /^(spalte|column)\s*\d+$/i.test(spalte.name.trim()),
          );

          return fromCriteria(
            [
              criterion(
                `Mindestens drei Spalten (gefunden in „${groesste.name}“: ${groesste.columns.length})`,
                groesste.columns.length >= 3,
              ),
              criterion(
                leere.length === 0
                  ? "Jede Spalte trägt eine Überschrift"
                  : `${leere.length} Spalte${leere.length === 1 ? "" : "n"} ohne Überschrift`,
                leere.length === 0,
              ),
              criterion(
                standardnamen.length === 0
                  ? "Keine Überschrift heißt noch „Spalte1“"
                  : `Von Excel vergeben: ${standardnamen.map((spalte) => `„${spalte.name}“`).join(", ")}`,
                standardnamen.length === 0,
              ),
              criterion(
                `Mindestens vier Datenzeilen (gefunden: ${groesste.rowCount})`,
                groesste.rowCount >= 4,
              ),
            ],
            {
              success:
                `„${groesste.name}“ taugt als Basis: ${groesste.columns.length} benannte Spalten ` +
                `(${groesste.columns.map((spalte) => spalte.name).join(", ")}) und ${groesste.rowCount} Datenzeilen.`,
              failure: "Die Datenbasis erfüllt noch nicht alle Anforderungen.",
              hint: "Aus den Spaltenüberschriften werden die Feldnamen der PivotTable. Fehlt eine, " +
                "verweigert Excel den Dienst.",
            },
          );
        },
      },
    },
    {
      id: "felder-verteilen",
      title: "Die vier Bereiche",
      explanation: [
        "„Einfügen“ → „PivotTable“ erzeugt ein leeres Gerüst und öffnet rechts die " +
          "Feldliste. Oben stehen die Felder – also die Spaltenüberschriften –, unten " +
          "vier Kästen: Filter, Spalten, Zeilen und Werte. Ein Feld wird per Ziehen in " +
          "einen dieser Kästen gelegt.",
        "Die Zuordnung folgt einer einfachen Logik. In die Zeilen kommt, wonach " +
          "gruppiert werden soll – meist eine Kategorie wie Region oder Produkt. In " +
          "die Werte kommt, was gerechnet werden soll, also eine Zahlenspalte. Diese " +
          "beiden genügen für die erste PivotTable; alles Weitere ist Verfeinerung.",
        "In die Spalten legt man ein zweites Gruppierungsfeld, wenn eine Kreuztabelle " +
          "entstehen soll: Region in den Zeilen, Monat in den Spalten, Umsatz in den " +
          "Werten. Das ist die Darstellung, für die man von Hand ein Dutzend " +
          "SUMMEWENNS bräuchte. Sparsam sein lohnt sich hier: Zwei Felder in den " +
          "Spalten ergeben eine Tabelle, die niemand mehr überblickt.",
        "Der Filterbereich blendet die ganze PivotTable auf einen Ausschnitt ein. Er " +
          "funktioniert, ist aber die unhandlichste der Varianten – die Datenschnitte " +
          "aus der nächsten Lektion können dasselbe und zeigen dabei, was gerade " +
          "eingestellt ist.",
        "Was in den Werten steht, rechnet Excel standardmäßig als Summe – außer bei " +
          "Textspalten, dort als Anzahl. Über einen Klick auf das Feld im " +
          "Wertebereich lässt sich das ändern, auf Mittelwert, Maximum oder auch auf " +
          "„Prozent des Gesamtergebnisses“, was für Anteile oft die aussagekräftigere " +
          "Darstellung ist.",
      ],
      keyPoints: [
        "Zeilen = wonach gruppiert wird, Werte = was gerechnet wird.",
        "Spalten nur für eine echte Kreuztabelle, und dann nur ein Feld.",
        "Klick auf das Wertefeld ändert die Rechenart und die Darstellung.",
      ],
      task: {
        instruction:
          "Erzeuge eine PivotTable mit mindestens einem Feld in den Zeilen und einem " +
          "im Wertebereich.",
        hint: "Cursor in die Tabelle, dann Einfügen → PivotTable. Rechts eine Kategorie in „Zeilen“ und eine Zahlenspalte in „Werte“ ziehen.",
        check: async () => {
          const daten = await readData();

          if (daten.pivotTables === null) {
            return nichtPruefbar(
              "Die PivotTables der Arbeitsmappe",
              "1.3",
              "Sieh nach, ob auf einem Blatt eine PivotTable steht.",
              true,
            );
          }

          if (daten.pivotTables.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine PivotTable.",
              "Setze den Cursor in deine Tabelle und wähle Einfügen → PivotTable.",
            );
          }

          const erste = daten.pivotTables[0];
          if (erste.rowFields === null || erste.dataFields === null) {
            return nichtPruefbar(
              "Der Aufbau einer PivotTable, also ihre Zeilen- und Wertefelder",
              "1.8",
              `Sieh in der Feldliste deiner PivotTable „${erste.name}“ nach, ob unter „Zeilen“ und ` +
                "unter „Werte“ je ein Feld liegt.",
              true,
            );
          }

          const aufgebaut = daten.pivotTables.filter(istAufgebaut);
          if (aufgebaut.length === 0) {
            return fail(
              `Die PivotTable „${erste.name}“ ist noch leer: ${erste.rowFields.length} Feld` +
                `${erste.rowFields.length === 1 ? "" : "er"} in den Zeilen, ` +
                `${erste.dataFields.length} im Wertebereich.`,
              "Ziehe in der Feldliste rechts eine Kategorie in den Kasten „Zeilen“ und eine " +
                "Zahlenspalte in den Kasten „Werte“.",
            );
          }

          const fertige = aufgebaut[0];
          return pass(
            `„${fertige.name}“ gruppiert nach ${fertige.rowFields?.map((feld) => `„${feld.name}“`).join(", ")} ` +
              `und rechnet ${fertige.dataFields?.map((feld) => `„${feld.name}“`).join(", ")}` +
              (fertige.columnFields && fertige.columnFields.length > 0
                ? `, aufgeteilt nach ${fertige.columnFields.map((feld) => `„${feld.name}“`).join(", ")}`
                : "") +
              ".",
          );
        },
      },
    },
  ],
};
